import React, { useCallback, useEffect, useState } from "react";
import api from "@/lib/api";
import { money, fmtDate } from "@/lib/format";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { Plus, Trash2, Check, TrendingDown, Layers, Trophy } from "lucide-react";
import { useDensity } from "@/lib/useDensity";
import { DensityToggle } from "@/components/ui/DensityToggle";
import { useChartTheme } from "@/lib/chartTheme";
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, Cell, CartesianGrid } from "recharts";

const slug = (s) => String(s).toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "");

// Structure-first bid leveling: scope groups are the backbone rows; vendors are columns.
export default function LevelingTab({ bid, readOnly }) {
  const [data, setData] = useState(null);
  const [open, setOpen] = useState(false);
  const [vendor, setVendor] = useState("");
  const [disc, setDisc] = useState("electrical");
  const bidId = bid.bid_id;

  const load = useCallback(() => {
    api.get(`/bids/${bidId}/leveling`).then((r) => setData(r.data)).catch(() => {});
  }, [bidId]);
  useEffect(() => { load(); }, [load]);

  const { compact, toggle: toggleDensity, rowPad, cellPad } = useDensity();
  const ct = useChartTheme();

  if (!data) return null;
  const { rows, vendors, matrix, row_low, awarded_total, low_total } = data;

  const addVendor = async () => {
    if (!vendor.trim()) { toast.error("Vendor name is required."); return; }
    try {
      await api.post(`/bids/${bidId}/quotes/add-vendor`, { vendor_name: vendor.trim(), discipline: disc });
      setOpen(false); setVendor(""); load(); toast.success(`Added ${vendor.trim()}`);
    } catch (e) { toast.error(e.response?.data?.detail || "Could not add vendor"); }
  };
  const removeVendor = async (name) => { await api.post(`/bids/${bidId}/quotes/remove-vendor`, { vendor_name: name }); load(); };
  const dstr = (iso) => (iso ? new Date(iso).toISOString().slice(0, 10) : "");
  const setValidity = async (name, v) => {
    try { await api.post(`/bids/${bidId}/quotes/vendor-validity`, { vendor_name: name, valid_until: v ? new Date(v + "T12:00:00").toISOString() : null }); load(); }
    catch { toast.error("Could not update quote validity"); }
  };
  const setAmount = async (group, v, cell, value) => {
    const amt = value === "" ? null : Number(value);
    try {
      if (cell.quote_id) await api.put(`/quotes/${cell.quote_id}`, { amount: amt });
      else await api.post(`/bids/${bidId}/quotes`, { group, vendor_name: v, discipline: vendors.find((x) => x.name === v)?.discipline || "mechanical", amount: amt });
      load();
    } catch (e) { toast.error("Could not save amount"); }
  };
  const award = async (cell) => { if (!cell.quote_id) return; await api.post(`/quotes/${cell.quote_id}/select`); load(); toast.success("Line awarded"); };

  return (
    <div className="p-6" data-testid="leveling-tab">
      <div className="flex items-center justify-between mb-4 gap-4">
        <div>
          <h2 className="text-sm font-semibold uppercase tracking-wider text-slate-600 flex items-center gap-2"><Layers className="w-4 h-4 text-[#003E87]" /> Bid leveling</h2>
          <p className="text-xs text-slate-500 mt-0.5 max-w-2xl">Structure-first — your scope groups are the backbone. Enter each vendor's number per line; the lowest is flagged, coverage gaps are called out, and you pick the award per line.</p>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <DensityToggle compact={compact} onToggle={toggleDensity} testid="leveling-density-toggle" />
          {!readOnly && <Button size="sm" onClick={() => setOpen(true)} className="bg-[#003E87] hover:bg-[#00305F]" data-testid="leveling-add-vendor"><Plus className="w-4 h-4 mr-1" /> Add vendor</Button>}
        </div>
      </div>

      {rows.length === 0 ? (
        <Card className="p-10 text-center text-sm text-slate-400" data-testid="leveling-no-scope">Add scope groups in the Estimating tab first — they form the leveling backbone.</Card>
      ) : vendors.length === 0 ? (
        <Card className="p-10 text-center text-sm text-slate-400" data-testid="leveling-empty">No vendors yet. Add a subcontractor to start comparing quotes line-for-line.</Card>
      ) : (
        <>
          <Card className="overflow-x-auto">
            <table className="w-full text-sm border-collapse" data-testid="leveling-matrix">
              <thead>
                <tr className="border-b border-slate-200 text-[11px] uppercase tracking-wide text-slate-400">
                  <th className={`text-left font-medium px-3 ${rowPad} sticky left-0 bg-white z-10 min-w-[200px]`}>Scope line</th>
                  {vendors.map((v) => (
                    <th key={v.name} className={`text-right font-medium px-3 ${rowPad} min-w-[150px]`} data-testid={`leveling-vendor-col-${slug(v.name)}`}>
                      <div className="flex items-center justify-end gap-1.5">
                        <span className="text-slate-700 normal-case text-xs font-semibold">{v.name}</span>
                        {!readOnly && <button onClick={() => removeVendor(v.name)} data-testid={`leveling-remove-vendor-${slug(v.name)}`} className="text-slate-300 hover:text-rose-600"><Trash2 className="w-3 h-3" /></button>}
                      </div>
                      <div className="text-[10px] text-slate-400 normal-case font-normal">{v.coverage_pct}% coverage</div>
                      <div className="mt-1 flex items-center justify-end gap-1 normal-case font-normal" data-testid={`leveling-validity-${slug(v.name)}`}>
                        {v.validity?.status === "expired" && <span className="text-[10px] text-rose-600 font-semibold">expired</span>}
                        {v.validity?.status === "expiring" && <span className="text-[10px] text-amber-600 font-semibold">{v.validity.days}d left</span>}
                        {v.validity?.status === "valid" && <span className="text-[10px] text-emerald-600">valid {v.validity.days}d</span>}
                        {!readOnly ? (
                          <input type="date" value={dstr(v.valid_until)} onChange={(e) => setValidity(v.name, e.target.value)} title="Quote valid until"
                            className="text-[10px] border border-slate-200 rounded px-1 py-0.5 text-slate-500 outline-none" data-testid={`leveling-valid-until-${slug(v.name)}`} />
                        ) : v.valid_until ? <span className="text-[10px] text-slate-400">to {fmtDate(v.valid_until)}</span> : null}
                      </div>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <tr key={r} className="border-b border-slate-50" data-testid={`leveling-row-${slug(r)}`}>
                    <td className={`px-3 ${cellPad} font-medium text-slate-800 sticky left-0 bg-white z-10`}>
                      {r}
                      {row_low[r] != null && <div className="text-[10px] text-emerald-600 font-mono">low {money(row_low[r])}</div>}
                    </td>
                    {vendors.map((v) => {
                      const c = matrix[r][v.name];
                      return (
                        <td key={v.name} className={`px-2 ${cellPad} text-right ${c.is_low && c.has_quote ? "bg-emerald-50/70" : ""} ${c.is_awarded ? "ring-1 ring-inset ring-emerald-400" : ""}`} data-testid={`leveling-cell-${slug(r)}-${slug(v.name)}`}>
                          <div className="flex items-center justify-end gap-1">
                            {c.is_low && c.has_quote && <TrendingDown className="w-3 h-3 text-emerald-600 shrink-0" title="Lowest" />}
                            <input type="number" defaultValue={c.amount ?? ""} disabled={readOnly}
                              onBlur={(e) => { const nv = e.target.value; if (String(c.amount ?? "") !== nv) setAmount(r, v.name, c, nv); }}
                              onKeyDown={(e) => { if (e.key === "Enter") e.target.blur(); }}
                              placeholder="—"
                              className={`w-24 text-right text-sm font-mono bg-transparent border border-transparent hover:border-slate-200 focus:border-[#003E87] rounded px-1.5 py-0.5 outline-none disabled:cursor-default ${c.has_quote ? "text-slate-900" : "text-slate-300"}`}
                              data-testid={`leveling-input-${slug(r)}-${slug(v.name)}`} />
                            {!readOnly && c.has_quote && (
                              <button onClick={() => award(c)} title={c.is_awarded ? "Awarded" : "Award this line to this vendor"} data-testid={`leveling-award-${slug(r)}-${slug(v.name)}`}
                                className={`shrink-0 ${c.is_awarded ? "text-emerald-600" : "text-slate-300 hover:text-emerald-600"}`}><Check className="w-3.5 h-3.5" /></button>
                            )}
                          </div>
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr className="border-t-2 border-slate-200 bg-slate-50/60 text-sm">
                  <td className={`px-3 ${rowPad} font-semibold text-slate-700 sticky left-0 bg-slate-50 z-10`}>Vendor total</td>
                  {vendors.map((v) => (
                    <td key={v.name} className={`px-3 ${rowPad} text-right font-mono font-semibold text-slate-900`} data-testid={`leveling-total-${slug(v.name)}`}>{money(v.total)}</td>
                  ))}
                </tr>
              </tfoot>
            </table>
          </Card>

          <div className="mt-4 grid sm:grid-cols-2 gap-3">
            <Card className="p-4 flex items-center gap-3" data-testid="leveling-award-total">
              <div className="w-9 h-9 rounded-lg bg-emerald-50 text-emerald-600 grid place-items-center"><Trophy className="w-5 h-5" /></div>
              <div>
                <div className="text-[11px] uppercase tracking-wide text-slate-400">Leveled award total</div>
                <div className="text-lg font-mono font-semibold text-slate-900">{money(awarded_total)}</div>
                <div className="text-[11px] text-slate-400">awarded per line, else lowest</div>
              </div>
            </Card>
            <Card className="p-4 flex items-center gap-3" data-testid="leveling-low-total">
              <div className="w-9 h-9 rounded-lg bg-[#003E87]/10 text-[#003E87] grid place-items-center"><TrendingDown className="w-5 h-5" /></div>
              <div>
                <div className="text-[11px] uppercase tracking-wide text-slate-400">All-low total</div>
                <div className="text-lg font-mono font-semibold text-slate-900">{money(low_total)}</div>
                <div className="text-[11px] text-slate-400">if every line went to its lowest bidder</div>
              </div>
            </Card>
          </div>

          {(() => {
            const totals = vendors.filter((v) => v.total > 0).map((v) => v.total);
            const minTotal = totals.length ? Math.min(...totals) : null;
            return (
              <Card className="mt-4 p-5" data-testid="leveling-vendor-compare">
                <h3 className="text-sm font-semibold uppercase tracking-wider text-slate-600 mb-1">Vendor total comparison</h3>
                <p className="text-xs text-slate-500 mb-3">Bars show each vendor's grand total across all lines — the lowest complete number is highlighted.</p>
                <ResponsiveContainer width="100%" height={Math.max(120, vendors.length * 46)}>
                  <BarChart layout="vertical" data={vendors.map((v) => ({ name: v.name, total: v.total || 0 }))} margin={{ top: 4, right: 28, bottom: 4, left: 8 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke={ct.grid} horizontal={false} />
                    <XAxis type="number" tickFormatter={(x) => (x >= 1e6 ? `$${(x / 1e6).toFixed(1)}M` : x >= 1e3 ? `$${Math.round(x / 1e3)}k` : `$${x}`)} tick={{ fontSize: 10, fill: ct.tickDim }} axisLine={false} tickLine={false} />
                    <YAxis type="category" dataKey="name" width={120} tick={{ fontSize: 11, fill: ct.tick }} axisLine={false} tickLine={false} />
                    <Tooltip formatter={(x) => money(x)} contentStyle={ct.contentStyle} itemStyle={ct.itemStyle} labelStyle={ct.labelStyle} cursor={{ fill: ct.cursor }} />
                    <Bar dataKey="total" radius={[0, 4, 4, 0]} maxBarSize={26}>
                      {vendors.map((v) => <Cell key={v.name} fill={minTotal != null && v.total === minTotal ? "#10B981" : ct.bar} />)}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </Card>
            );
          })()}
        </>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="leveling-add-vendor-dialog">
          <DialogHeader><DialogTitle>Add a subcontractor / vendor</DialogTitle>
            <DialogDescription>A column is added across every scope line so you can fill in their number per line.</DialogDescription></DialogHeader>
          <div className="space-y-3">
            <div><Label className="text-xs">Vendor name</Label><Input data-testid="leveling-vendor-name" value={vendor} onChange={(e) => setVendor(e.target.value)} placeholder="Acme Civil" /></div>
            <div><Label className="text-xs">Primary discipline</Label>
              <Select value={disc} onValueChange={setDisc}>
                <SelectTrigger data-testid="leveling-vendor-discipline"><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="mechanical">Mechanical</SelectItem><SelectItem value="electrical">Electrical</SelectItem><SelectItem value="civil">Civil</SelectItem></SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button><Button onClick={addVendor} className="bg-[#003E87] hover:bg-[#00305F]" data-testid="leveling-vendor-submit">Add vendor</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
